import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Upload, X, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface Application {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  organization: string;
  role: string;
  created_at: string;
  updated_at: string;
}

const VerificationApplication: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [existingApplications, setExistingApplications] = useState<Application[]>([]);
  const [hasPendingApplication, setHasPendingApplication] = useState(false);
  const [formData, setFormData] = useState({
    organization: '',
    role: '',
    experience: '',
    portfolio: '',
    linkedin: '',
    pressCard: null as File | null,
    additionalInfo: ''
  });

  useEffect(() => {
    const fetchApplications = async () => {
      if (!currentUser) return;

      try {
        const { data, error } = await supabase
          .from('verification_applications')
          .select('id, status, organization, role, created_at, updated_at')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        setExistingApplications(data || []);
        setHasPendingApplication(data?.some(app => app.status === 'pending') || false);
      } catch (err) {
        console.error('Error fetching applications:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplications();
  }, [currentUser]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF, JPG, or PNG file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setFormData(prev => ({ ...prev, pressCard: file }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    setIsSubmitting(true);
    setError('');

    try {
      // Upload press card if provided
      let pressCardUrl = '';
      if (formData.pressCard) {
        const fileExt = formData.pressCard.name.split('.').pop();
        const fileName = `${currentUser.id}/${crypto.randomUUID()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('verification-docs')
          .upload(fileName, formData.pressCard);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('verification-docs')
          .getPublicUrl(fileName);

        pressCardUrl = publicUrl;
      }

      // Submit verification application
      const { error: submitError } = await supabase
        .from('verification_applications')
        .insert({
          user_id: currentUser.id,
          organization: formData.organization,
          role: formData.role,
          experience: formData.experience,
          portfolio_url: formData.portfolio,
          linkedin_url: formData.linkedin,
          press_card_url: pressCardUrl,
          additional_info: formData.additionalInfo
        });

      if (submitError) throw submitError;

      // Redirect to home with success message
      navigate('/home', { 
        state: { 
          message: 'Your verification application has been submitted successfully. We will review it and get back to you soon.' 
        }
      });
    } catch (err: any) {
      console.error('Error submitting application:', err);
      if (err.message?.includes('duplicate') || err.code === '23505') {
        setError('You already have a pending application. Please wait for it to be reviewed.');
      } else {
        setError('Failed to submit application. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-400" />;
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-400" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-900/50 border-yellow-700 text-yellow-200';
      case 'approved':
        return 'bg-green-900/50 border-green-700 text-green-200';
      case 'rejected':
        return 'bg-red-900/50 border-red-700 text-red-200';
      default:
        return '';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (currentUser?.is_verified) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            <span>Back</span>
          </button>
          <Shield className="h-8 w-8 text-purple-400" />
        </div>
        <div className="bg-[#272727] rounded-xl p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">You're Already Verified!</h2>
          <p className="text-gray-400 mb-6">
            Your account has been verified as a professional journalist.
          </p>
          <button
            onClick={() => navigate('/home')}
            className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span>Back</span>
        </button>

        <Shield className="h-8 w-8 text-purple-400" />
      </div>

      <div className="bg-[#272727] rounded-xl p-6">
        <h1 className="text-2xl font-bold text-white mb-2">
          Journalist Verification Application
        </h1>
        <p className="text-gray-400 mb-6">
          Complete this form to apply for journalist verification. We'll review your application
          and respond within 5-7 business days.
        </p>

        {existingApplications.length > 0 && (
          <div className="mb-6 space-y-3">
            <h3 className="text-sm font-medium text-gray-300">Your Applications</h3>
            {existingApplications.map((app) => (
              <div
                key={app.id}
                className={`p-4 rounded-lg border flex items-start justify-between ${getStatusColor(app.status)}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusIcon(app.status)}
                    <span className="font-medium capitalize">{app.status}</span>
                  </div>
                  <p className="text-sm opacity-90">
                    {app.organization} - {app.role}
                  </p>
                  <p className="text-xs opacity-75 mt-1">
                    Submitted {new Date(app.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {hasPendingApplication && (
          <div className="mb-6 bg-yellow-900/50 border border-yellow-700 text-yellow-200 px-4 py-3 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-1">Pending Application</p>
              <p className="text-sm opacity-90">
                You have a pending verification application. You can submit a new application
                once your current application is reviewed.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="organization" className="block text-sm font-medium text-gray-300 mb-2">
              Media Organization
            </label>
            <input
              type="text"
              id="organization"
              value={formData.organization}
              onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))}
              className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#404040] hover:border-[#505050] transition-colors"
              placeholder="Enter your media organization"
              required
            />
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-300 mb-2">
              Role/Position
            </label>
            <input
              type="text"
              id="role"
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#404040] hover:border-[#505050] transition-colors"
              placeholder="e.g., Reporter, Editor, Freelance Journalist"
              required
            />
          </div>

          <div>
            <label htmlFor="experience" className="block text-sm font-medium text-gray-300 mb-2">
              Years of Experience
            </label>
            <input
              type="number"
              id="experience"
              min="0"
              max="100"
              value={formData.experience}
              onChange={(e) => setFormData(prev => ({ ...prev, experience: e.target.value }))}
              className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#404040] hover:border-[#505050] transition-colors"
              required
            />
          </div>

          <div>
            <label htmlFor="portfolio" className="block text-sm font-medium text-gray-300 mb-2">
              Portfolio/Website URL
            </label>
            <input
              type="url"
              id="portfolio"
              value={formData.portfolio}
              onChange={(e) => setFormData(prev => ({ ...prev, portfolio: e.target.value }))}
              className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#404040] hover:border-[#505050] transition-colors"
              placeholder="https://"
            />
          </div>

          <div>
            <label htmlFor="linkedin" className="block text-sm font-medium text-gray-300 mb-2">
              LinkedIn Profile URL
            </label>
            <input
              type="url"
              id="linkedin"
              value={formData.linkedin}
              onChange={(e) => setFormData(prev => ({ ...prev, linkedin: e.target.value }))}
              className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#404040] hover:border-[#505050] transition-colors"
              placeholder="https://linkedin.com/in/..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Press Card or Professional ID
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="hidden"
                id="pressCard"
              />
              <label
                htmlFor="pressCard"
                className="flex items-center justify-center w-full px-4 py-4 bg-[#272727] text-white rounded-lg border-2 border-dashed border-[#404040] hover:border-[#505050] transition-colors cursor-pointer"
              >
                {formData.pressCard ? (
                  <div className="flex items-center">
                    <span className="text-gray-300">{formData.pressCard.name}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setFormData(prev => ({ ...prev, pressCard: null }));
                      }}
                      className="ml-2 text-gray-400 hover:text-white"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-gray-400">Upload document (PDF, JPG, PNG)</span>
                  </div>
                )}
              </label>
            </div>
          </div>

          <div>
            <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-300 mb-2">
              Additional Information
            </label>
            <textarea
              id="additionalInfo"
              value={formData.additionalInfo}
              onChange={(e) => setFormData(prev => ({ ...prev, additionalInfo: e.target.value }))}
              className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#404040] hover:border-[#505050] transition-colors min-h-[100px] resize-none"
              placeholder="Any additional information to support your application..."
            />
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isSubmitting || hasPendingApplication}
              className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:bg-purple-400/50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerificationApplication;